import { useState } from "react";

import { useSubmitPresentationReview } from "@/api/presentation-review";
import type { PresentationReviewResponse } from "@/api/types";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

/**
 * Approve / reject footer for the M2 presentation review surface
 * (issue #144). Single decision per presentation, with an optional
 * notes field (always shown so the reviewer can attach rationale on
 * either path — the rejection case is the common one).
 *
 * On 409 `already_decided` (server-side race lost), surface a refresh
 * prompt rather than a retry button — the row is terminal and the
 * UI cache is stale.
 */
export interface ReviewDecisionPanelProps {
  presentation: PresentationReviewResponse;
}

export function ReviewDecisionPanel({ presentation }: ReviewDecisionPanelProps) {
  const submit = useSubmitPresentationReview(presentation.id);
  const [notes, setNotes] = useState("");
  // `pendingDecision` tracks which button the user is mid-submit on so we
  // can render a spinner on the right button without disabling the other
  // pre-click. Cleared on success or error.
  const [pendingDecision, setPendingDecision] = useState<
    "approved" | "rejected" | null
  >(null);

  const alreadyDecided =
    presentation.status === "approved" ||
    presentation.status === "rejected" ||
    presentation.status === "expired" ||
    presentation.status === "failed";

  if (alreadyDecided) {
    return (
      <div className="rounded-md border border-border p-4 space-y-2">
        <div className="text-sm">
          <span className="font-medium">Decision recorded:</span>{" "}
          <span className="font-mono">{presentation.status}</span>
          {presentation.reviewedBy && (
            <>
              {" by "}
              <span>{presentation.reviewedBy}</span>
            </>
          )}
          {presentation.reviewedAt && (
            <>
              {" at "}
              <span>{new Date(presentation.reviewedAt).toLocaleString()}</span>
            </>
          )}
        </div>
        {presentation.reviewNotes && (
          <div className="text-sm whitespace-pre-wrap">
            <span className="font-medium">Notes:</span> {presentation.reviewNotes}
          </div>
        )}
      </div>
    );
  }

  const handleSubmit = (decision: "approved" | "rejected") => {
    setPendingDecision(decision);
    submit.mutate(
      { decision, ...(notes.trim().length > 0 ? { notes: notes.trim() } : {}) },
      {
        onSettled: () => setPendingDecision(null),
      },
    );
  };

  const errorMessage = submit.error instanceof Error ? submit.error.message : null;
  const isAlreadyDecidedRace = errorMessage?.includes("already_decided");

  return (
    <div className="rounded-md border border-border p-4 space-y-3">
      <div>
        <label
          htmlFor="presentation-review-notes"
          className="block text-xs font-medium uppercase tracking-wider text-text-muted"
        >
          Notes (optional)
        </label>
        {/* Explicit admin-theme tokens (bg-bg-card / text-text /
            border-border) instead of the raw shadcn `bg-background` /
            `border-input` — the admin's dark theme leaves the shadcn
            names unresolved which surfaced as white-on-dark
            unreadable input (issue #575, finding #5). Same idiom as
            the ManualRun textarea in `routes/ManualRun.tsx:248`. */}
        <textarea
          id="presentation-review-notes"
          className="mt-1 w-full rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
          rows={3}
          maxLength={5000}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional rationale (recommended on rejection)…"
        />
      </div>

      {submit.isError && (
        <Alert variant="destructive">
          {isAlreadyDecidedRace
            ? "This presentation was decided in another session. Refresh to see the current status."
            : (errorMessage ?? "Failed to submit decision.")}
        </Alert>
      )}

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => handleSubmit("rejected")}
          disabled={submit.isPending}
        >
          {pendingDecision === "rejected" ? "Rejecting…" : "Reject"}
        </Button>
        <Button
          onClick={() => handleSubmit("approved")}
          disabled={submit.isPending}
        >
          {pendingDecision === "approved" ? "Approving…" : "Approve"}
        </Button>
      </div>
    </div>
  );
}
